import React, { useState } from "react"
import {
  ColumnDef,
  ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import Loader from "./loader"

interface TableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData>[]
  isLoading?: boolean
  columnOrder?: ColumnOrderState
  columnVisibility?: VisibilityState
  initialSorting?: SortingState
  onRowClick?: (row: TData) => void
  emptyMessage?: string
  className?: string
  headerClassName?: string
  bodyClassName?: string
  rowClassName?: string
  cellClassName?: string
}

function Table<TData>({
  data,
  columns,
  isLoading = false,
  columnOrder,
  columnVisibility,
  initialSorting = [],
  onRowClick,
  emptyMessage = "No data found.",
  className = "",
  headerClassName = "",
  bodyClassName = "",
  rowClassName = "",
  cellClassName = "",
}: TableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting)

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl border-none">
        <div className="flex h-64 items-center justify-center">
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className={`w-full min-w-[600px] divide-y divide-gray-200 ${className}`}>
        <thead className={headerClassName}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`whitespace-nowrap px-4 py-3 text-left text-sm font-normal ${
                    header.column.getIsSorted()
                      ? "font-bold text-black dark:text-white"
                      : "text-gray-500 dark:text-gray-300"
                  }`}
                >
                  <div
                    className={`flex select-none items-center ${
                      header.column.getCanSort() ? "cursor-pointer" : "cursor-default"
                    }`}
                    onClick={header.column.getToggleSortingHandler()}
                    title={
                      header.column.getCanSort()
                        ? header.column.getNextSortingOrder() === "asc"
                          ? "Sort ascending"
                          : header.column.getNextSortingOrder() === "desc"
                            ? "Sort descending"
                            : "Clear sort"
                        : undefined
                    }
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span className="ml-1 place-content-center">
                      {header.column.getIsSorted() === "asc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="size-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                      ) : header.column.getIsSorted() === "desc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="size-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      ) : null}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={`divide-y divide-gray-200 bg-white ${bodyClassName}`}>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-8 text-center text-gray-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-[#18181b] ${rowClassName}`}
                onClick={(e) => {
                  // Don't trigger row click if clicking on interactive elements
                  if ((e.target as HTMLElement).closest("button, a, input, select, textarea")) {
                    return
                  }
                  onRowClick?.(row.original)
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`whitespace-nowrap px-3 py-2 ${cellClassName}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

export { Table }
export type { TableProps }
